import { SENDA } from '@somnhold/content';
import { idiv } from '../math/fixed.ts';
import { rngInt, seedRng, deriveSeed } from '../math/rng.ts';

/**
 * The counterweight (docs/sendas/guerrero.md §4, «El contrapeso», chapter 2): three spans in a row, each hung from its
 * pan; each post says its number, and a span comes down level when what pulls it equals its post. The rule, carved
 * on the beam in two lines:
 *
 * - the middle pan pulls the outer ones: each outer one adds half of what the middle weighs, rounded down;
 * - the outer ones pull the middle up: the middle loses a quarter of what the two outer ones weigh together, rounded
 *   down.
 *
 * The pieces are the five shields (1, 2, 3, 3 and 4, scratched on the rim) and two stones of 2. The five shields all
 * hang (the Company leaves nobody on the ground); the stones, if they're needed. The generator tries every way of
 * hanging them (3⁵ for the shields × 4² for the stones, a stone left on the ground being the fourth place) and keeps
 * the posts that one share of weights alone levels, built in one to three ways from the pieces; the obvious count
 * (each pan its own post) is never it, since the outer pans always pull the middle. Integers only, from the seed:
 * the server builds the same.
 */

/** What each span feels from the pans' raw weights (left, middle, right). */
export const weightPull = (l: number, m: number, r: number): [number, number, number] => [l + (m >> 1), m - ((l + r) >> 2), r + (m >> 1)];

export interface WeightPiece {
  w: number;
  stone: boolean;
}

/** The pieces, shields first (in the order of their weights), then the stones. */
export const WEIGHT_PIECES: WeightPiece[] = [...SENDA.weight.shields.map((w) => ({ w, stone: false })), ...SENDA.weight.stones.map((w) => ({ w, stone: true }))];

const SHIELDS = SENDA.weight.shields.length;
const STONES = SENDA.weight.stones.length;

/** One way of hanging the pieces: each piece's pan (0 to 2), or 3 for a stone left on the ground. */
export type WeightHang = number[];

const key3 = (a: number, b: number, c: number): number => (a * 64 + b) * 64 + c;

interface Table {
  /** Posts → the shares of weights that level them (as key3) and the ways to hang the pieces for each. */
  byPosts: Map<number, Map<number, WeightHang[]>>;
}

/** Every way of hanging the pieces, worked once (3⁵ × 4² = 3888). */
const TABLE: Table = (() => {
  const byPosts = new Map<number, Map<number, WeightHang[]>>();
  let n = 1;
  for (let i = 0; i < SHIELDS; i++) n *= 3;
  for (let i = 0; i < STONES; i++) n *= 4;
  for (let code = 0; code < n; code++) {
    let c = code;
    const hang: number[] = [];
    for (let i = 0; i < SHIELDS; i++) {
      hang.push(c % 3);
      c = idiv(c, 3);
    }
    for (let i = 0; i < STONES; i++) {
      hang.push(c % 4);
      c = idiv(c, 4);
    }
    const w = [0, 0, 0];
    hang.forEach((p, i) => {
      if (p < 3) w[p] = (w[p] as number) + (WEIGHT_PIECES[i] as WeightPiece).w;
    });
    const [l, m, r] = w as [number, number, number];
    const [pl, pm, pr] = weightPull(l, m, r);
    if (pl < 1 || pm < 1 || pr < 1) continue;
    const pk = key3(pl, pm, pr);
    let shares = byPosts.get(pk);
    if (!shares) byPosts.set(pk, (shares = new Map()));
    const wk = key3(l, m, r);
    let ways = shares.get(wk);
    if (!ways) shares.set(wk, (ways = []));
    ways.push(hang);
  }
  return { byPosts };
})();

/** A way of hanging, told apart from another only by which weights go where (the two 3s and the two stones are alike). */
const wayKey = (h: WeightHang): string =>
  [0, 1, 2]
    .map((p) =>
      h
        .map((x, i) => (x === p ? `${(WEIGHT_PIECES[i] as WeightPiece).stone ? 's' : ''}${(WEIGHT_PIECES[i] as WeightPiece).w}` : ''))
        .filter(Boolean)
        .sort()
        .join('+'),
    )
    .join('|');

/** The shares of weights (left, middle, right) that level these posts, each with its distinct ways to hang the pieces. */
export const weightSolutions = (posts: [number, number, number]): { w: [number, number, number]; ways: WeightHang[] }[] => {
  const shares = TABLE.byPosts.get(key3(posts[0], posts[1], posts[2]));
  if (!shares) return [];
  const out: { w: [number, number, number]; ways: WeightHang[] }[] = [];
  for (const [wk, ways] of [...shares].sort((a, b) => a[0] - b[0])) {
    const seen = new Map<string, WeightHang>();
    for (const h of ways) if (!seen.has(wayKey(h))) seen.set(wayKey(h), h);
    out.push({ w: [idiv(wk, 4096), idiv(wk, 64) % 64, wk % 64], ways: [...seen.values()] });
  }
  return out;
};

export interface WeightLayout {
  posts: [number, number, number];
  /** The one share of weights that levels them. */
  answer: [number, number, number];
  /** The ways to hang the pieces for it (one to three, told apart by weights). */
  ways: WeightHang[];
}

/** Whether these posts make a fair counterweight: one share of weights, one to three ways, the pans pulling both ways. */
export const weightFair = (posts: [number, number, number]): boolean => {
  const sol = weightSolutions(posts);
  if (sol.length !== 1) return false;
  const [l, m, r] = (sol[0] as { w: [number, number, number] }).w;
  const ways = (sol[0] as { ways: WeightHang[] }).ways.length;
  // Posts a person reads at a glance (3 to 12), the pans pulling both ways.
  return ways >= 1 && ways <= SENDA.weight.ways && m >= 2 && l + r >= 4 && l >= 1 && r >= 1 && posts.every((p) => p >= 3 && p <= 12);
};

/** Every fair set of posts, in a fixed order (the seed picks one). */
export const WEIGHT_POSTS: [number, number, number][] = (() => {
  const out: [number, number, number][] = [];
  for (const pk of [...TABLE.byPosts.keys()].sort((a, b) => a - b)) {
    const p: [number, number, number] = [idiv(pk, 4096), idiv(pk, 64) % 64, pk % 64];
    if (weightFair(p)) out.push(p);
  }
  return out;
})();

/** The counterweight of a fight's seed. */
export const weightLayout = (seed: number): WeightLayout => {
  const rng = seedRng(deriveSeed(seed, 0x7c41));
  const posts = WEIGHT_POSTS[rngInt(rng, WEIGHT_POSTS.length)] as [number, number, number];
  const sol = weightSolutions(posts)[0] as { w: [number, number, number]; ways: WeightHang[] };
  return { posts, answer: sol.w, ways: sol.ways };
};
